/**
 * The inspector for a selected node: its header (name, kind, categories), then sections that
 * fold (`Fold.tsx`, remembered per environment): Details, Data, Links, Choices, Files,
 * Comments; an empty one is a single "+ Add …" line. Each section is its own file, reads the
 * node through the same accessor and edits it through `actions`.
 */
import { Show, createMemo, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { undeclared } from "../../shared/fields";
import { Fold } from "./Fold";
import { NodeActions } from "./NodeActions";
import { NodeComments } from "./NodeComments";
import { NodeData } from "./NodeData";
import { NodeDescription, NodeKindAndCategories } from "./NodeFields";
import { NodeGroup } from "./NodeGroup";
import { NodeHeader } from "./NodeHeader";
import { NodeLinkForm } from "./NodeLinkForm";
import { NodePins } from "./NodePins";
import { NodeFiles } from "./NodeFiles";
import { InheritanceChain, NodeNeighbours } from "./NodeRelations";

/** Everything about one node. */
export function NodePanel(props: { id: string }): JSX.Element {
  const { graph, fields, access } = useApp();
  const node = createMemo(() => graph.state.graph.nodes.find((n) => n.id === props.id));
  /** How many links touch the node. */
  const linkCount = createMemo(() => graph.state.graph.links.filter((l) => l.from === props.id || l.to === props.id).length);
  const canEdit = () => access.can("editor");
  return (
    <Show when={node()}>
      {(n) => (
        <>
          <NodeHeader node={n} />
          <Fold id="details" title="Details">
            <NodeDescription node={n} />
            <NodeKindAndCategories node={n} />
            <NodeGroup node={n} />
          </Fold>
          <Show when={fields.list().length || undeclared(n(), fields.list()).length}>
            <Fold id="data" title="Data">
              <NodeData node={n} />
            </Fold>
          </Show>
          <Fold id="links" title="Links" count={linkCount()} empty={!linkCount()} add={canEdit() ? "Add a link" : undefined}>
            <InheritanceChain node={n} />
            <NodeNeighbours node={n} />
            <Show when={canEdit()}>
              <NodeLinkForm node={n} />
            </Show>
          </Fold>
          <NodePins node={n} />
          <Show when={(n().attachments ?? []).length || canEdit()}>
            <Fold id="files" title="Files" count={(n().attachments ?? []).length} empty={!(n().attachments ?? []).length} add="Attach a file">
              <NodeFiles node={n} />
            </Fold>
          </Show>
          <Fold id="comments" title="Comments" count={n().comments.length} empty={!n().comments.length} add="Add a comment">
            <NodeComments node={n} />
          </Fold>
          <NodeActions node={n} />
        </>
      )}
    </Show>
  );
}
